// @flow

import React, { Component } from 'react';
import ListView from './list/ListView';
import type { PanelRegistry, RegistryEntityActionCollection } from './registry';
import { SuperRegistry } from './registry';
import reduceList, { getDefaultListState } from '../store/list/reducers';
import CreateForm from './create/CreateForm';
import EditForm from './edit/EditForm';
import InfoView from './info/InfoView';
import UserSessionsPanel from './info/UserSessionsPanel';
import SessionBillLink from './info/SessionBillLink';
import BillLinkedPanel from './info/BillLinkedPanel';
import BillPaymentsPanel from './info/BillPaymentsPanel';
import OrganizationCommercialTermsPanel from './info/OrganizationCommercialTermsPanel';
import ContactRequestDashboard from './contactRequests/ContactRequestDashboard';
import OperationsOverviewDashboard from './operations/OperationsOverviewDashboard';
import RestaurantActivityDashboard from './operations/RestaurantActivityDashboard';
import SupportMonitoringDashboard from './operations/SupportMonitoringDashboard';
import LaunchStatusDashboard from './operations/LaunchStatusDashboard';
import MenuCatalogDashboard from './menu/MenuCatalogDashboard';
import reduceCreateForm, { getDefaultCreateFormState } from '../store/create/reducers';
import reduceEditForm, { getDefaultEditFormState } from '../store/edit/reducers';
import reduceInfo, { getDefaultInfoState } from '../store/info/reducers';
import type { StoredEntity } from '../entities/abstract/StoredEntity';
import type { ListAction } from '../store/list/types';
import type { RowAction } from './types';
import Admin from '../entities/models/Admin';
import AdminRT from '../entities/models/AdminRT';
import Beacon from '../entities/models/Beacon';
import Bill from '../entities/models/Bill';
import ContactRequest from '../entities/models/ContactRequest';
import LocationSession from '../entities/models/LocationSession';
import Organization from '../entities/models/Organization';
import OrganizationUser from '../entities/models/OrganizationUser';
import OrgLocation from '../entities/models/OrgLocation';
import OrgUserRT from '../entities/models/OrgUserRT';
import User from '../entities/models/User';
import PaymentMethod from '../entities/models/PaymentMethod';
import PhoneCheck from '../entities/models/PhoneCheck';
import Policy from '../entities/models/Policy';
import RewardAccount from '../entities/models/RewardAccount';
import RewardTransaction from '../entities/models/RewardTransaction';
import Transaction from '../entities/models/Transaction';
import AuthSession from '../entities/models/AuthSession';
import OrgReview from '../entities/models/OrgReview';

type ModeOptions = {
    canViewExecutiveReport?: boolean,
};

//
//
// MODE TYPEDEF
//
//

// 1 - Display in navbar
// 2 - Display in navbar dropdown
// undefined - N/A
export type ModePriority = 1 | 2 | undefined;

// A Bill of data about any given standalone page
export type Mode = {
    // CORE
    component: Component,
    priority: ModePriority,
    // TITLE
    navbarTitle: ?string,
    // ROUTER
    routePath: string,
    // REDUX
    initialStateGetter: () => any,
    reducer: (state: any, action: any) => void,
};

export type ModeCollection = {
    [key: string]: Mode,
};

export type StandardModeType = 'list' | 'info' | 'create' | 'edit';

//
//
// HELPERS
//
//

function getStandardModeName(entityType: $Shape<StoredEntity>, modeType: StandardModeType) {
    return `${entityType.classInternalName}-${modeType}`;
}

function getStandardModeHandler(entityType: $Shape<StoredEntity>, modeType: StandardModeType) {
    const root = `/${getStandardModeName(entityType, modeType)}`;

    switch (modeType) {
        case 'create':
        case 'list':
            return (history: any) => {
                history.push(root);
            };
        case 'info':
        case 'edit':
            return (history: any, id: string) => {
                history.push(`${root}/${id}`);
            };
        default:
            return undefined;
    }
}

function getStandardModeUrl(entityType: $Shape<StoredEntity>, modeType: StandardModeType) {
    const root = `/${getStandardModeName(entityType, modeType)}`;

    switch (modeType) {
        case 'create':
        case 'list':
            return root;
        case 'info':
        case 'edit':
            return `${root}/:id`;
        default:
            return undefined;
    }
}

//
//
// MODE GETTERS
//
//

export function getListMode(
    entityType: $Shape<StoredEntity>,
    canCreate: boolean,
    canUpdate: boolean,
    canDelete: boolean,
    priority: ModePriority,
    panelTitleGetter: () => string,
    navbarTitle: ?string,
    customListActions: [ListAction] = [],
    customRowActions: [RowAction] = [],
): Mode {
    // List actions

    let listActions = [];

    if (canCreate) {
        const createNewAction = {
            text: 'Create New',
            handler: getStandardModeHandler(entityType, 'create'),
        };
        listActions = [createNewAction];
    }

    if (customListActions) {
        listActions = [...listActions, ...customListActions];
    }

    // Row actions

    const infoAction = {
        text: 'Info',
        modal: true,
        handler: getStandardModeHandler(entityType, 'info'),
    };

    let rowActions = [infoAction];

    if (canUpdate) {
        rowActions.push({
            text: 'Edit',
            modal: true,
            handler: getStandardModeHandler(entityType, 'edit'),
        });
    }

    if (customRowActions) {
        rowActions = [...rowActions, ...customRowActions];
    }

    return {
        component: (
            <ListView
                model={entityType}
                modeName={getStandardModeName(entityType, 'list')}
                listActions={listActions}
                rowActions={rowActions}
                titleGetter={panelTitleGetter}
                canDelete={canDelete}
            />
        ),
        priority,
        navbarTitle,
        initialStateGetter: getDefaultListState,
        reducer: reduceList,
        routePath: getStandardModeUrl(entityType, 'list'),
    };
}

function getListModeFromRegistry(
    registry: RegistryEntityActionCollection,
    entityType: $Shape<StoredEntity>,
    priority: ModePriority,
    panelTitleGetter: () => string,
    navbarTitle: string,
    customListActions: [ListAction] = [],
    customRowActions: [RowAction] = [],
) {
    const registryRecord = registry[entityType.classInternalName];

    if (registryRecord.operations.r) {
        return getListMode(
            entityType,
            registryRecord.operations.c,
            registryRecord.operations.u,
            registryRecord.operations.d,
            priority,
            panelTitleGetter,
            navbarTitle,
            customListActions,
            customRowActions,
        );
    }
    return null;
}

export function getInfoMode(entityType: $Shape<StoredEntity>, panelTitleGetter: (entity: StoredEntity) => string) {
    return {
        component: (
            <InfoView
                model={entityType}
                modeName={getStandardModeName(entityType, 'info')}
                actions={[]}
                titleGetter={panelTitleGetter}
            />
        ),
        priority: undefined,
        navbarTitle: null,
        initialStateGetter: getDefaultInfoState,
        reducer: reduceInfo,
        routePath: getStandardModeUrl(entityType, 'info'),
    };
}

function getInfoModeFromRegistry(
    registry: RegistryEntityActionCollection,
    entityType: $Shape<StoredEntity>,
    panelTitleGetter: (entity: StoredEntity) => string,
) {
    const registryRecord = registry[entityType.classInternalName];

    if (registryRecord.operations.r) {
        return getInfoMode(entityType, panelTitleGetter);
    }
    return null;
}

export function getCreateMode(entityType: $Shape<StoredEntity>, panelTitleGetter: () => string) {
    return {
        component: (
            <CreateForm
                model={entityType}
                modeName={getStandardModeName(entityType, 'create')}
                onSuccess={getStandardModeHandler(entityType, 'list')}
                titleGetter={panelTitleGetter}
            />
        ),
        priority: undefined,
        panelTitleGetter,
        navbarTitle: null,
        initialStateGetter: getDefaultCreateFormState,
        reducer: reduceCreateForm,
        routePath: getStandardModeUrl(entityType, 'create'),
    };
}

function getCreateModeFromRegistry(
    registry: RegistryEntityActionCollection,
    entityType: $Shape<StoredEntity>,
    panelTitleGetter: (entity: StoredEntity) => string,
) {
    const registryRecord = registry[entityType.classInternalName];

    if (registryRecord.operations.c) {
        return getCreateMode(entityType, panelTitleGetter);
    }
    return null;
}

export function getEditMode(entityType: $Shape<StoredEntity>, panelTitleGetter: (entity: StoredEntity) => string) {
    return {
        component: (
            <EditForm
                model={entityType}
                modeName={getStandardModeName(entityType, 'edit')}
                onSuccess={getStandardModeHandler(entityType, 'list')}
                titleGetter={panelTitleGetter}
            />
        ),
        priority: undefined,
        panelTitleGetter,
        navbarTitle: null,
        initialStateGetter: getDefaultEditFormState,
        reducer: reduceEditForm,
        routePath: getStandardModeUrl(entityType, 'edit'),
    };
}

function getEditModeFromRegistry(
    registry: RegistryEntityActionCollection,
    entityType: $Shape<StoredEntity>,
    panelTitleGetter: () => string,
) {
    const registryRecord = registry[entityType.classInternalName];

    if (registryRecord.operations.u) {
        return getEditMode(entityType, panelTitleGetter);
    }
    return null;
}

export function getAllModes(registry: PanelRegistry, options: ModeOptions = {}): ModeCollection {
    const reac = registry === undefined ? new SuperRegistry().entityActions : registry.entityActions;
    const { canViewExecutiveReport = true } = options;

    const customModes = {
        'operations-dashboard': {
            component: <OperationsOverviewDashboard />,
            priority: 1,
            navbarTitle: 'Operations',
            initialStateGetter: getDefaultListState,
            reducer: reduceList,
            routePath: '/operations-dashboard',
        },
        'restaurant-activity': {
            component: <RestaurantActivityDashboard />,
            priority: 2,
            navbarTitle: 'Restaurant Pulse',
            initialStateGetter: getDefaultListState,
            reducer: reduceList,
            routePath: '/restaurant-activity',
        },
        'support-monitor': {
            component: <SupportMonitoringDashboard />,
            priority: 1,
            navbarTitle: 'Support Hub',
            initialStateGetter: getDefaultListState,
            reducer: reduceList,
            routePath: '/support-monitor',
        },
        'menu-catalog': {
            component: <MenuCatalogDashboard />,
            priority: 1,
            navbarTitle: 'Menus',
            initialStateGetter: getDefaultListState,
            reducer: reduceList,
            routePath: '/menu-catalog',
        },
    };

    if (canViewExecutiveReport) {
        customModes['launch-status'] = {
            component: <LaunchStatusDashboard />,
            priority: 1,
            navbarTitle: 'Launch Status',
            initialStateGetter: getDefaultListState,
            reducer: reduceList,
            routePath: '/launch-status',
        };
    }

    const byEntity = {
        [Admin.classInternalName]: {
            list: getListModeFromRegistry(reac, Admin, 2, () => 'Admin List', 'Admins'),
            info: getInfoModeFromRegistry(reac, Admin, (entity: StoredEntity) => `${entity.displayName}`),
            create: getCreateModeFromRegistry(reac, Admin, () => `New Admin`),
            edit: getEditModeFromRegistry(reac, Admin, (entity: StoredEntity) => `Edit ${entity.displayName}`),
        },
        [AdminRT.classInternalName]: {
            list: getListModeFromRegistry(reac, AdminRT, 2, () => 'Admin RT List', 'Admin RTs'),
            info: getInfoModeFromRegistry(reac, AdminRT, (entity: StoredEntity) => `${entity.displayName}`),
            create: getCreateModeFromRegistry(reac, AdminRT, () => `New Admin RT`),
            edit: getEditModeFromRegistry(reac, AdminRT, (entity: StoredEntity) => `Edit ${entity.displayName}`),
        },
        [Beacon.classInternalName]: {
            list: getListModeFromRegistry(reac, Beacon, 2, () => 'Beacon List', 'Beacons'),
            info: getInfoModeFromRegistry(reac, Beacon, (entity: StoredEntity) => `${entity.displayName}`),
            create: getCreateModeFromRegistry(reac, Beacon, () => `New Beacon`),
            edit: getEditModeFromRegistry(reac, Beacon, (entity: StoredEntity) => `Edit ${entity.displayName}`),
        },
        [ContactRequest.classInternalName]: {
            list: (() => {
                const registryRecord = reac[ContactRequest.classInternalName];
                if (!registryRecord.operations.r) return null;
                return {
                    component: <ContactRequestDashboard />,
                    priority: 1,
                    navbarTitle: 'Contact Requests',
                    initialStateGetter: getDefaultListState,
                    reducer: reduceList,
                    routePath: getStandardModeUrl(ContactRequest, 'list'),
                };
            })(),
            info: getInfoModeFromRegistry(reac, ContactRequest, (entity: StoredEntity) => `${entity.displayName}`),
            create: getCreateModeFromRegistry(reac, ContactRequest, () => 'New Contact Request'),
            edit: getEditModeFromRegistry(reac, ContactRequest, (entity: StoredEntity) => `Edit ${entity.displayName}`),
        },
        [Bill.classInternalName]: {
            list: getListModeFromRegistry(reac, Bill, 1, () => 'Bill List', 'Bills'),
            info: (() => {
                const registryRecord = reac[Bill.classInternalName];
                if (!registryRecord.operations.r) return null;
                return {
                    component: (
                        <InfoView
                            model={Bill}
                            modeName={getStandardModeName(Bill, 'info')}
                            actions={[]}
                            titleGetter={(entity: StoredEntity) => `${entity.displayName}`}
                            renderExtra={(entity: StoredEntity) => (
                                <div>
                                    <BillPaymentsPanel billId={entity.id} />
                                    <BillLinkedPanel
                                        billId={entity.id}
                                        sessionId={entity.record['location_session_id']}
                                        userId={entity.record['user_id']}
                                        payerUserId={entity.record['payer_user_id']}
                                        coveredByName={entity.record['covered_by_name']}
                                    />
                                </div>
                            )}
                        />
                    ),
                    priority: undefined,
                    navbarTitle: null,
                    initialStateGetter: getDefaultInfoState,
                    reducer: reduceInfo,
                    routePath: getStandardModeUrl(Bill, 'info'),
                };
            })(),
            create: getCreateModeFromRegistry(reac, Bill, () => `New Bill`),
            edit: getEditModeFromRegistry(reac, Bill, (entity: StoredEntity) => `Edit ${entity.displayName}`),
        },
        [LocationSession.classInternalName]: {
            list: getListModeFromRegistry(reac, LocationSession, 1, () => 'Location Session List', 'Location Sessions'),
            info: (() => {
                const registryRecord = reac[LocationSession.classInternalName];
                if (!registryRecord.operations.r) return null;
                return {
                    component: (
                        <InfoView
                            model={LocationSession}
                            modeName={getStandardModeName(LocationSession, 'info')}
                            actions={[]}
                            titleGetter={(entity: StoredEntity) => `${entity.displayName}`}
                            renderExtra={(entity: StoredEntity) => <SessionBillLink sessionId={entity.id} />}
                        />
                    ),
                    priority: undefined,
                    navbarTitle: null,
                    initialStateGetter: getDefaultInfoState,
                    reducer: reduceInfo,
                    routePath: getStandardModeUrl(LocationSession, 'info'),
                };
            })(),
            create: getCreateModeFromRegistry(reac, LocationSession, () => `New Location Session`),
            edit: getEditModeFromRegistry(
                reac,
                LocationSession,
                (entity: StoredEntity) => `Edit ${entity.displayName}`,
            ),
        },
        [Organization.classInternalName]: {
            list: getListModeFromRegistry(reac, Organization, 1, () => 'Organization List', 'Organizations'),
            info: (() => {
                const registryRecord = reac[Organization.classInternalName];
                if (!registryRecord.operations.r) return null;
                return {
                    component: (
                        <InfoView
                            model={Organization}
                            modeName={getStandardModeName(Organization, 'info')}
                            actions={[]}
                            titleGetter={(entity: StoredEntity) => `${entity.displayName}`}
                            renderExtra={(entity: StoredEntity) => (
                                <OrganizationCommercialTermsPanel organization={entity} />
                            )}
                        />
                    ),
                    priority: undefined,
                    navbarTitle: null,
                    initialStateGetter: getDefaultInfoState,
                    reducer: reduceInfo,
                    routePath: getStandardModeUrl(Organization, 'info'),
                };
            })(),
            create: getCreateModeFromRegistry(reac, Organization, () => `New Organization`),
            edit: getEditModeFromRegistry(reac, Organization, (entity: StoredEntity) => `Edit ${entity.displayName}`),
        },
        [OrganizationUser.classInternalName]: {
            list: getListModeFromRegistry(
                reac,
                OrganizationUser,
                2,
                () => 'Organization User List',
                'Organization Users',
            ),
            info: getInfoModeFromRegistry(reac, OrganizationUser, (entity: StoredEntity) => `${entity.displayName}`),
            create: getCreateModeFromRegistry(reac, OrganizationUser, () => `New Organization User`),
            edit: getEditModeFromRegistry(
                reac,
                OrganizationUser,
                (entity: StoredEntity) => `Edit ${entity.displayName}`,
            ),
        },
        [OrgLocation.classInternalName]: {
            list: getListModeFromRegistry(reac, OrgLocation, 1, () => 'Location List', 'Locations'),
            info: getInfoModeFromRegistry(reac, OrgLocation, (entity: StoredEntity) => `${entity.displayName}`),
            create: getCreateModeFromRegistry(reac, OrgLocation, () => `New Location`),
            edit: getEditModeFromRegistry(reac, OrgLocation, (entity: StoredEntity) => `Edit ${entity.displayName}`),
        },
        [OrgReview.classInternalName]: {
            list: getListModeFromRegistry(reac, OrgReview, 2, () => 'Review List', 'Reviews'),
            info: getInfoModeFromRegistry(reac, OrgReview, (entity: StoredEntity) => `${entity.displayName}`),
            create: getCreateModeFromRegistry(reac, OrgReview, () => `New Review`),
            edit: getEditModeFromRegistry(reac, OrgReview, (entity: StoredEntity) => `Edit ${entity.displayName}`),
        },
        [OrgUserRT.classInternalName]: {
            list: getListModeFromRegistry(reac, OrgUserRT, 2, () => 'Org User RT List', 'Org User RTs'),
            info: getInfoModeFromRegistry(reac, OrgUserRT, (entity: StoredEntity) => `${entity.displayName}`),
            create: getCreateModeFromRegistry(reac, OrgUserRT, () => `New Org User RT`),
            edit: getEditModeFromRegistry(reac, OrgUserRT, (entity: StoredEntity) => `Edit ${entity.displayName}`),
        },
        [PaymentMethod.classInternalName]: {
            list: getListModeFromRegistry(reac, PaymentMethod, 2, () => 'Payment Method List', 'Payment Methods'),
            info: getInfoModeFromRegistry(reac, PaymentMethod, (entity: StoredEntity) => `${entity.displayName}`),
            create: getCreateModeFromRegistry(reac, PaymentMethod, () => `New Payment Method`),
            edit: getEditModeFromRegistry(reac, PaymentMethod, (entity: StoredEntity) => `Edit ${entity.displayName}`),
        },
        [PhoneCheck.classInternalName]: {
            list: getListModeFromRegistry(reac, PhoneCheck, 2, () => 'Phone Check List', 'Phone Checks'),
            info: getInfoModeFromRegistry(reac, PhoneCheck, (entity: StoredEntity) => `${entity.displayName}`),
            create: getCreateModeFromRegistry(reac, PhoneCheck, () => `New Phone Check`),
            edit: getEditModeFromRegistry(reac, PhoneCheck, (entity: StoredEntity) => `Edit ${entity.displayName}`),
        },
        [Policy.classInternalName]: {
            list: getListModeFromRegistry(reac, Policy, 2, () => 'Policy List', 'Policies'),
            info: getInfoModeFromRegistry(reac, Policy, (entity: StoredEntity) => `${entity.displayName}`),
            create: getCreateModeFromRegistry(reac, Policy, () => `New Policy`),
            edit: getEditModeFromRegistry(reac, Policy, (entity: StoredEntity) => `Edit ${entity.displayName}`),
        },
        [RewardAccount.classInternalName]: {
            list: getListModeFromRegistry(reac, RewardAccount, 2, () => 'Reward Account List', 'Reward Accounts'),
            info: getInfoModeFromRegistry(reac, RewardAccount, (entity: StoredEntity) => `${entity.displayName}`),
            create: getCreateModeFromRegistry(reac, RewardAccount, () => `New Reward Account`),
            edit: getEditModeFromRegistry(reac, RewardAccount, (entity: StoredEntity) => `Edit ${entity.displayName}`),
        },
        [RewardTransaction.classInternalName]: {
            list: getListModeFromRegistry(
                reac,
                RewardTransaction,
                2,
                () => 'Reward Transaction List',
                'Reward Transactions',
            ),
            info: getInfoModeFromRegistry(reac, RewardTransaction, (entity: StoredEntity) => `${entity.displayName}`),
            create: getCreateModeFromRegistry(reac, RewardTransaction, () => `New Reward Transaction`),
            edit: getEditModeFromRegistry(
                reac,
                RewardTransaction,
                (entity: StoredEntity) => `Edit ${entity.displayName}`,
            ),
        },
        [Transaction.classInternalName]: {
            list: getListModeFromRegistry(reac, Transaction, 2, () => 'Transaction List', 'Transactions'),
            info: getInfoModeFromRegistry(reac, Transaction, (entity: StoredEntity) => `${entity.displayName}`),
            create: getCreateModeFromRegistry(reac, Transaction, () => `New Transaction`),
            edit: getEditModeFromRegistry(reac, Transaction, (entity: StoredEntity) => `Edit ${entity.displayName}`),
        },
        [User.classInternalName]: {
            list: getListModeFromRegistry(reac, User, 1, () => 'User List', 'Users'),
            info: (() => {
                const registryRecord = reac[User.classInternalName];
                if (!registryRecord.operations.r) return null;
                return {
                    component: (
                        <InfoView
                            model={User}
                            modeName={getStandardModeName(User, 'info')}
                            actions={[]}
                            titleGetter={(entity: StoredEntity) => `${entity.displayName}`}
                            renderExtra={(entity: StoredEntity) => <UserSessionsPanel userId={entity.id} />}
                        />
                    ),
                    priority: undefined,
                    navbarTitle: null,
                    initialStateGetter: getDefaultInfoState,
                    reducer: reduceInfo,
                    routePath: getStandardModeUrl(User, 'info'),
                };
            })(),
            create: getCreateModeFromRegistry(reac, User, () => `New User`),
            edit: getEditModeFromRegistry(reac, User, (entity: StoredEntity) => `Edit ${entity.displayName}`),
        },
        [AuthSession.classInternalName]: {
            list: getListModeFromRegistry(reac, AuthSession, 2, () => 'Auth Session List', 'Auth Sessions'),
            info: getInfoModeFromRegistry(reac, AuthSession, (entity: StoredEntity) => `${entity.displayName}`),
            create: getCreateModeFromRegistry(reac, AuthSession, () => `New Auth Session`),
            edit: getEditModeFromRegistry(reac, AuthSession, (entity: StoredEntity) => `Edit ${entity.displayName}`),
        },
    };

    const modes = { ...customModes };
    for (const [entityName, entityMode] of Object.entries(byEntity)) {
        for (const [modeName, mode] of Object.entries(entityMode)) {
            if (mode !== null) {
                modes[`${entityName}-${modeName}`] = mode;
            }
        }
    }

    return modes;
}
